import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Label } from '@hero/web-components';
import { AlertCircleIcon, ArrowLeftIcon, ArrowRightIcon, HandCoinsIcon } from '@hero/web-icons';
import {
  useAgreementListApi,
  useClientApi,
  useInvoiceApi,
  useOrgProfileApi,
} from '@hero/api-hooks';
import type { Id } from '@hero/schema';
import {
  TERM_DURATION_OPTIONS,
  type FinancingProfileMock,
  type TermDurationMock,
} from '../../mocks';
import { computeHardBlocks, getInvoiceExpectedDueDate, pickCurrentAgreement } from '../Requirements';
import type { ApplyFormSubmitPayload, ApplyFormValues } from './types';
import { isFinancingProfileComplete } from '../Requirements';
import { InvoiceFieldset } from './InvoiceFieldset';
import { ReviewStep } from './ReviewStep';
import { TermsFieldset } from './TermsFieldset';
import { WizardStepper, type WizardStepKey } from './WizardStepper';

const INITIAL_VALUES: ApplyFormValues = {
  fleetTier: '',
  annualRevenueTier: '',
  financingNeeds: [],
  ownsAllTrucks: '',
  trucksOwnedCount: '',
  hasLoans: '',
  monthlyFinancingPayment: '',
  isProfitable: '',
  financialStatements: [],

  invoiceTab: 'external',
  heroInvoiceId: '',
  heroInvoiceAmount: '',
  heroInvoiceCustomerName: '',
  heroInvoiceNumber: '',
  heroExpectedDueDate: null,
  externalClientId: '',
  externalCustomerName: '',
  externalAmount: '',
  externalExpectedDueDate: null,
  externalInvoiceNumber: '',
  externalDocument: null,

  requestedAmount: '',
  requestedAmountSameAsInvoice: true,
  requestedTermKey: '60_days',
};

/**
 * SessionStorage key for the in-progress wizard draft. Bumped if
 * the shape of `ApplyFormValues` changes incompatibly so stale
 * drafts get discarded rather than re-hydrated into a broken state.
 */
const DRAFT_STORAGE_KEY = 'financing.apply.draft.v3';

/** Hero invoice picker fields — never persisted; only URL deep link pre-selects. */
const NON_PERSISTED_DRAFT_KEYS = new Set([
  'externalDocument',
  'financialStatements',
  'heroInvoiceId',
  'heroInvoiceAmount',
  'heroInvoiceCustomerName',
  'heroInvoiceNumber',
  'heroExpectedDueDate',
  'externalExpectedDueDate',
]);

/** Keys we DO persist — `File` instances can't survive a reload. */
type SerializableKey = Exclude<
  keyof ApplyFormValues,
  | 'externalDocument'
  | 'financialStatements'
  | 'heroExpectedDueDate'
  | 'externalExpectedDueDate'
  | 'heroInvoiceId'
  | 'heroInvoiceAmount'
  | 'heroInvoiceCustomerName'
  | 'heroInvoiceNumber'
>;

function resetHeroInvoiceFields(values: ApplyFormValues): ApplyFormValues {
  return {
    ...values,
    invoiceTab: 'hero',
    heroInvoiceId: '',
    heroInvoiceAmount: '',
    heroInvoiceCustomerName: '',
    heroInvoiceNumber: '',
    heroExpectedDueDate: null,
  };
}

/**
 * Read the saved draft (if any) and merge it onto `INITIAL_VALUES`.
 * Dates are revived from ISO strings; `File` fields are reset because
 * the browser can't rehydrate them across reload. The optional
 * `initialValues` patch (from `Org.financingProfile`) wins over
 * `INITIAL_VALUES` but loses to the user's in-progress draft — once
 * the carrier starts editing, we don't clobber their work.
 */
function loadDraft(initialValues?: Partial<ApplyFormValues>): ApplyFormValues {
  const base: ApplyFormValues = {
    ...INITIAL_VALUES,
    ...(initialValues ?? {}),
  };
  if (typeof window === 'undefined') return base;
  try {
    const raw = window.sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<
      Omit<ApplyFormValues, 'externalDocument' | 'financialStatements'>
    > & {
      heroExpectedDueDate?: string | null;
      externalExpectedDueDate?: string | null;
    };
    return {
      ...base,
      ...parsed,
      heroInvoiceId: '',
      heroInvoiceAmount: '',
      heroInvoiceCustomerName: '',
      heroInvoiceNumber: '',
      heroExpectedDueDate: null,
      externalExpectedDueDate: parsed.externalExpectedDueDate
        ? new Date(parsed.externalExpectedDueDate)
        : parsed.externalDueDate
          ? new Date(parsed.externalDueDate)
          : null,
      externalDocument: null,
      financialStatements: [],
    };
  } catch {
    return base;
  }
}

function saveDraft(values: ApplyFormValues) {
  if (typeof window === 'undefined') return;
  try {
    const serializable: Record<string, unknown> = {};
    (Object.keys(values) as Array<keyof ApplyFormValues>).forEach(key => {
      if (NON_PERSISTED_DRAFT_KEYS.has(key)) return;
      serializable[key as SerializableKey] = values[key as SerializableKey];
    });
    window.sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    // sessionStorage can throw in private mode / quota — degrade silently.
  }
}

function clearDraft() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

const STEP_QUERY_KEY = 'apply_step';
const ALL_STEP_KEYS: WizardStepKey[] = ['invoice', 'terms', 'review'];
const parseStepParam = (
  value: string | null,
  allowed: readonly WizardStepKey[],
): WizardStepKey => {
  const normalized = value === 'profile' ? 'terms' : value;
  return (allowed as readonly string[]).includes(normalized ?? '')
    ? ((normalized as WizardStepKey) ?? 'invoice')
    : 'invoice';
};

const termFromKey = (key: string): TermDurationMock => {
  const map: Record<string, TermDurationMock> = {
    '30_days': { value: 30, unit: 'days', label: '30 days' },
    '60_days': { value: 60, unit: 'days', label: '60 days' },
    '90_days': { value: 90, unit: 'days', label: '90 days' },
    '6_months': { value: 6, unit: 'months', label: '6 months' },
    '1_year': { value: 1, unit: 'year', label: '1 year' },
  };
  return map[key] ?? TERM_DURATION_OPTIONS[1];
};

/**
 * Per-step validity guards. Used to gate the Continue button.
 *
 * For the invoice step, "validity" goes deeper than "has the user
 * picked one?" on the Hero tab — the lender packet needs the
 * invoice details (VAT PDF + ref + pay date), the signed contract,
 * and the buyer's legal hard fields, all complete. We pass those
 * extra signals through `heroGates` rather than re-reading the
 * invoice + client + agreement inside the guard.
 */
const isStepValid = (
  step: WizardStepKey,
  v: ApplyFormValues,
  savedProfileComplete: boolean,
  heroGates?: { proofComplete: boolean; clientGatesComplete: boolean },
): boolean => {
  switch (step) {
    case 'invoice':
      if (v.invoiceTab === 'hero') {
        if (!v.heroInvoiceId) return false;
        // Hero gates default to "satisfied" while their queries
        // haven't resolved (heroGates === undefined). Once we
        // have an answer, both must be `true` to clear Continue.
        if (!heroGates) return true;
        return heroGates.proofComplete && heroGates.clientGatesComplete;
      }
      return Boolean(
        v.externalDocument &&
          v.externalClientId &&
          v.externalCustomerName.trim() &&
          v.externalAmount &&
          v.externalExpectedDueDate,
      );
    case 'terms':
      return (
        Boolean(v.requestedAmount) &&
        Boolean(v.requestedTermKey) &&
        savedProfileComplete
      );
    case 'review':
      return (
        isStepValid('invoice', v, savedProfileComplete, heroGates) &&
        isStepValid('terms', v, savedProfileComplete, heroGates) &&
        savedProfileComplete
      );
  }
};


export type ApplyFormHandle = {
  resetInvoiceSelection: () => void;
  scrollIntoView: (options?: ScrollIntoViewOptions) => void;
};

export type ApplyFormProps = {
  /**
   * Called when the user submits a complete application. Receives
   * the normalised payload that maps onto the eventual
   * `POST /financing/applications` body.
   */
  onSubmit: (payload: ApplyFormSubmitPayload) => void | Promise<void>;
  /** Disables the submit button while the parent's mutation runs. */
  isSubmitting?: boolean;
  /**
   * Optional initial values to pre-fill the wizard with — typically
   * the carrier's stored `Org.financingProfile`. Loses to an
   * in-progress draft in `sessionStorage`.
   */
  initialValues?: Partial<ApplyFormValues>;
};

/**
 * Compact 3-step financing apply wizard.
 *
 * Steps: Invoice → Terms (requirements + advance) → Review.
 * Each step is a single panel — the user only ever sees one
 * fieldset at a time, which keeps the form short and scannable
 * regardless of viewport. The Review step shows a summary card so
 * the carrier can spot a wrong number before submitting.
 *
 * `forwardRef` exposes {@link ApplyFormHandle} so the parent can
 * scroll the form into view or reset the hero invoice picker.
 */
export const ApplyForm = forwardRef<ApplyFormHandle, ApplyFormProps>(function ApplyForm(
  { onSubmit, isSubmitting = false, initialValues },
  ref,
) {
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useTranslation('m-financing');
  /**
   * Read the carrier's `Org.financingProfile` off the unified
   * `useOrgProfileApi()` flat projection. The dedicated
   * `/financing/profile` endpoint is gone — updates now flow through
   * `PATCH /org/profile` and reads come back on the same response.
   */
  const orgProfileQuery = useOrgProfileApi();
  const savedFinancingProfile = orgProfileQuery.data?.financingProfile ?? null;
  const savedProfileComplete = useMemo(
    () => isFinancingProfileComplete(savedFinancingProfile),
    [savedFinancingProfile],
  );

  /**
   * Draft persistence — values live in `sessionStorage` so a hard
   * refresh, a backend error, or jumping to a different tab and
   * back doesn't wipe what the carrier typed. File handles are
   * intentionally NOT persisted (the browser can't keep the bytes
   * alive across a reload).
   */
  const [values, setValues] = useState<ApplyFormValues>(() => loadDraft(initialValues));
  useEffect(() => {
    saveDraft(values);
  }, [values]);

  useImperativeHandle(ref, () => ({
    resetInvoiceSelection: () => {
      setValues(prev => resetHeroInvoiceFields(prev));
    },
    scrollIntoView: options => {
      sectionRef.current?.scrollIntoView(options);
    },
  }));

  const STEP_KEYS = ALL_STEP_KEYS;

  /**
   * Step lives in the URL (`?apply_step=...`) so back/forward and
   * deep-linked drafts (e.g. "open the profile step") work without
   * any extra state plumbing.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const step = parseStepParam(searchParams.get(STEP_QUERY_KEY), STEP_KEYS);

  /** Legacy deep links used `apply_step=profile` — normalize to Terms. */
  useEffect(() => {
    if (searchParams.get(STEP_QUERY_KEY) === 'profile') {
      setSearchParams(
        prev => {
          const sp = new URLSearchParams(prev);
          sp.set(STEP_QUERY_KEY, 'terms');
          return sp;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  /**
   * Bilateral invoice deep link — clicking "Finance it now" on a
   * shared `InvoiceDrawer` lands the carrier here with
   * `?invoiceId=<bilateralInvoiceId>`. Pre-select the Hero invoice
   * tab and seed the picker with the chosen invoice so the wizard
   * opens straight on Terms after one click. Strip the param once
   * we've applied it so refreshing doesn't re-seed over user edits.
   * See `docs/relations-v2/10`.
   */
  useEffect(() => {
    const invoiceId = searchParams.get('invoiceId');
    if (!invoiceId) return;
    setValues(prev =>
      prev.heroInvoiceId === invoiceId && prev.invoiceTab === 'hero'
        ? prev
        : { ...prev, invoiceTab: 'hero', heroInvoiceId: invoiceId },
    );
    setSearchParams(
      prev => {
        const sp = new URLSearchParams(prev);
        sp.delete('invoiceId');
        return sp;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const setStep = useCallback(
    (next: WizardStepKey) => {
      setSearchParams(
        prev => {
          const sp = new URLSearchParams(prev);
          if (next === 'invoice') sp.delete(STEP_QUERY_KEY);
          else sp.set(STEP_QUERY_KEY, next);
          return sp;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * Track which steps the user has cleared so the stepper can show
   * green check-marks and allow jump-back navigation. Re-derived
   * from the URL step on hydration so a draft-with-step-3 URL still
   * lights up steps 1+2.
   */
  const [completed, setCompleted] = useState<Set<WizardStepKey>>(() => {
    const idx = ALL_STEP_KEYS.indexOf(step);
    return new Set(ALL_STEP_KEYS.slice(0, Math.max(0, idx)));
  });

  /**
   * Accuracy confirmation — the carrier must explicitly attest that
   * the information is correct before we send it to lenders. Gates
   * the submit button on the review step.
   */
  const [confirmedAccuracy, setConfirmedAccuracy] = useState(false);
  useEffect(() => {
    if (step !== 'review') setConfirmedAccuracy(false);
  }, [step]);

  const onChange = <K extends keyof ApplyFormValues>(
    key: K,
    value: ApplyFormValues[K],
  ) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  /**
   * Keep `requestedAmount` in lock-step with the invoice amount when
   * the carrier hasn't opted into a partial advance. Runs whenever
   * the source amount changes (Hero invoice pick OR external-amount
   * typing) so the wizard's Terms step never shows a stale number.
   * When the toggle is off the user owns `requestedAmount` directly
   * and we leave it alone.
   */
  useEffect(() => {
    if (!values.requestedAmountSameAsInvoice) return;
    const sourceAmount =
      values.invoiceTab === 'hero'
        ? values.heroInvoiceAmount
        : values.externalAmount;
    if (sourceAmount !== values.requestedAmount) {
      setValues(prev => ({ ...prev, requestedAmount: sourceAmount }));
    }
  }, [
    values.requestedAmountSameAsInvoice,
    values.invoiceTab,
    values.heroInvoiceAmount,
    values.externalAmount,
    values.requestedAmount,
  ]);

  /**
   * Hard-block evaluation lives at the form level (not inside the
   * individual fieldsets) because the submit button needs the same
   * answer regardless of which step the user is on. TanStack Query
   * dedupes by query key so the duplicate calls here + inside
   * Invoice/Terms fieldsets share a single network round-trip.
   *
   * Scope:
   *  - Carrier legal + banking — always applicable.
   *  - Client legal + agreement — applicable on both tabs:
   *    * external tab → from the typed-in `externalClientId`.
   *    * hero tab     → from `invoice.buyer._id` when the buyer
   *      resolves to a Client (the dominant case).
   *  - Hero invoice proof — only on the hero tab.
   *
   * IMPORTANT: this block has to live BEFORE `heroGates` /
   * `stepValid` / `allValid` below — the latter read
   * `hardBlocks.*`, so reversing the order trips a TDZ
   * `Cannot access 'hardBlocks' before initialization`.
   */
  const profileQuery = orgProfileQuery;
  const heroInvoiceQuery = useInvoiceApi(
    values.invoiceTab === 'hero' && values.heroInvoiceId
      ? (values.heroInvoiceId as unknown as Id)
      : undefined,
  );
  const heroInvoice = heroInvoiceQuery.data;

  /**
   * Keep the Hero-tab expected due date snapshot aligned with the
   * live `Invoice.period.due`. After the proof drawer saves, the
   * invoice query invalidates and this effect refreshes the form
   * field + review summary so submit stays in sync.
   */
  useEffect(() => {
    if (values.invoiceTab !== 'hero' || !values.heroInvoiceId) return;
    const liveDue = getInvoiceExpectedDueDate(heroInvoice);
    if (!liveDue) return;
    setValues(prev => {
      if (prev.heroExpectedDueDate?.getTime() === liveDue.getTime()) return prev;
      return { ...prev, heroExpectedDueDate: liveDue };
    });
  }, [values.invoiceTab, values.heroInvoiceId, heroInvoice]);

  /**
   * Resolve the buyer-as-Client id for the picked Hero invoice
   * (when applicable). The invoice beneficiary still uses the
   * legacy `business_customer` / `individual_customer` target
   * enum (see
   * `apps/backend/src/app/payment/invoice/invoice.beneficiary.service.ts
   * → partyTargetToBeneficiaryTarget`), so checking
   * `target === 'client'` misses the CRM-buyer case entirely.
   * Read the `client_id` directly off `buyer.ids[]` — the
   * contract leg's tenant builder always writes it when the
   * buyer is sourced from CRM. Non-CRM buyers (Org / Driver /
   * raw User) yield `undefined` and skip the gates.
   */
  const heroBuyerClientId = useMemo(() => {
    if (values.invoiceTab !== 'hero' || !heroInvoice?.buyer) return undefined;
    const buyer = heroInvoice.buyer;
    const fromIds = buyer.ids?.find(e => e.type === 'client_id')?.id;
    if (fromIds) return fromIds;
    if (buyer.target === 'client') return String(buyer._id);
    return undefined;
  }, [values.invoiceTab, heroInvoice]);
  const gateClientId =
    values.invoiceTab === 'external'
      ? values.externalClientId || undefined
      : heroBuyerClientId;

  const clientQuery = useClientApi(gateClientId);
  const agreementListQuery = useAgreementListApi(
    { partyId: gateClientId },
    { enabled: !!gateClientId },
  );
  const currentAgreement = useMemo(
    () => pickCurrentAgreement(agreementListQuery.data ?? []),
    [agreementListQuery.data],
  );

  const hardBlocks = useMemo(
    () =>
      computeHardBlocks({
        profile: profileQuery.data,
        client: clientQuery.data,
        agreement: currentAgreement,
        heroInvoice: values.invoiceTab === 'hero' ? heroInvoice : undefined,
      }),
    [
      profileQuery.data,
      clientQuery.data,
      currentAgreement,
      heroInvoice,
      values.invoiceTab,
    ],
  );

  /**
   * Hero-tab gate signals fed into `isStepValid`. Derived from
   * the same hard-block computation that powers the submit
   * banner, so the Continue button on the invoice step and the
   * Submit button on the review step react to the exact same
   * truth.
   */
  const heroGates = useMemo(() => {
    if (values.invoiceTab !== 'hero') return undefined;
    return {
      proofComplete: !hardBlocks.heroInvoiceProof,
      clientGatesComplete: !hardBlocks.clientLegal && !hardBlocks.agreement,
    };
  }, [
    values.invoiceTab,
    hardBlocks.heroInvoiceProof,
    hardBlocks.clientLegal,
    hardBlocks.agreement,
  ]);

  const stepValid = useMemo(
    () => isStepValid(step, values, savedProfileComplete, heroGates),
    [step, values, savedProfileComplete, heroGates],
  );
  const allValid = useMemo(
    () => isStepValid('review', values, savedProfileComplete, heroGates),
    [values, savedProfileComplete, heroGates],
  );
  /**
   * `effectiveHardBlocked` collapses the per-leg flags into the
   * single boolean the submit button + banner switch on. Each
   * tab gates on its own subset:
   *  - Both tabs: carrier legal + carrier banking + client legal +
   *    agreement (the latter two only fire when we have a client
   *    id to evaluate against).
   *  - Hero tab only: the hero-invoice proof completeness.
   */
  const heroGatesApply = values.invoiceTab === 'hero';
  const effectiveHardBlocked =
    hardBlocks.carrierLegal ||
    hardBlocks.carrierBanking ||
    (!!gateClientId && (hardBlocks.clientLegal || hardBlocks.agreement)) ||
    (heroGatesApply && hardBlocks.heroInvoiceProof);

  /**
   * Auto-correct the wizard step on mount: if sessionStorage
   * hydration restored a draft past the invoice step but a required
   * field (notably the invoice file, which can't be persisted) is
   * missing, snap back to the first invalid step. Without this the
   * user lands on Review with a silently-disabled submit and no
   * indication of what's wrong.
   *
   * Intentionally runs only once — after the first render, the user
   * owns step navigation. We also keep `step === 'invoice'` as a
   * no-op so we never bounce a fresh user away from step 1.
   */
  const [didAutoCorrect, setDidAutoCorrect] = useState(false);
  useEffect(() => {
    if (didAutoCorrect) return;
    setDidAutoCorrect(true);
    if (step === 'invoice') return;
    const firstInvalid: WizardStepKey | undefined = STEP_KEYS.find(
      s => s !== 'review' && !isStepValid(s, values, savedProfileComplete, heroGates),
    );
    if (firstInvalid && STEP_KEYS.indexOf(firstInvalid) < STEP_KEYS.indexOf(step)) {
      setStep(firstInvalid);
    }
  }, [didAutoCorrect, step, values, setStep, STEP_KEYS, savedProfileComplete]);

  const goNext = () => {
    if (!stepValid) return;
    const idx = STEP_KEYS.indexOf(step);
    if (idx < STEP_KEYS.length - 1) {
      setCompleted(prev => new Set(prev).add(step));
      setStep(STEP_KEYS[idx + 1]);
    }
  };

  const goBack = () => {
    const idx = STEP_KEYS.indexOf(step);
    if (idx > 0) setStep(STEP_KEYS[idx - 1]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!allValid || isSubmitting || effectiveHardBlocked) return;
    if (!confirmedAccuracy) return;

    /**
     * For the Hero tab we read the snapshotted values written by
     * `InvoiceFieldset.onHeroInvoicePick`, with `expectedDueDate`
     * taken from live `Invoice.period.due` at submit so the proof
     * drawer stays the source of truth.
     */
    const isHero = values.invoiceTab === 'hero';

    const clientName = isHero
      ? values.heroInvoiceCustomerName
      : values.externalCustomerName;

    const amount = isHero
      ? Number(values.heroInvoiceAmount) || 0
      : Number(values.externalAmount);

    const expectedDueDate = isHero
      ? (getInvoiceExpectedDueDate(heroInvoice) ??
          values.heroExpectedDueDate)?.toISOString().slice(0, 10) ?? ''
      : (values.externalExpectedDueDate?.toISOString().slice(0, 10) ?? '');

    const profile: FinancingProfileMock = {
      fleetTier: values.fleetTier || 'lt_10',
      annualRevenueTier: values.annualRevenueTier || 'lt_1m',
      financingNeeds: values.financingNeeds,
      ownsAllTrucks: values.ownsAllTrucks === 'yes',
      ...(values.ownsAllTrucks === 'no' && {
        trucksOwnedCount: Number(values.trucksOwnedCount),
      }),
      hasLoans: values.hasLoans === 'yes',
      monthlyFinancingPayment:
        values.hasLoans === 'yes' ? Number(values.monthlyFinancingPayment) : undefined,
    };

    const resolvedClientId =
      values.invoiceTab === 'external'
        ? values.externalClientId
        : heroBuyerClientId;

    if (!resolvedClientId) return;

    const payload: ApplyFormSubmitPayload = {
      invoice: {
        source: values.invoiceTab,
        heroInvoice: isHero
          ? {
              id: values.heroInvoiceId,
              clientName: values.heroInvoiceCustomerName,
              invoiceNumber: values.heroInvoiceNumber,
              amount,
              currency: 'SAR',
              expectedDueDate,
            }
          : undefined,
        clientId: resolvedClientId,
        clientName,
        amount,
        expectedDueDate,
        invoiceNumber: isHero
          ? values.heroInvoiceNumber || undefined
          : values.externalInvoiceNumber || undefined,
        documentName: isHero ? undefined : values.externalDocument?.name,
        documentFile: isHero ? undefined : values.externalDocument ?? undefined,
      },
      requestedAmount: Number(values.requestedAmount),
      requestedTerm: termFromKey(values.requestedTermKey),
      profile,
      isProfitable:
        values.isProfitable === ''
          ? undefined
          : values.isProfitable === 'yes',
      financialStatements: values.financialStatements,
    };

    try {
      await onSubmit(payload);
    } catch {
      // Submission failed (the page-level handler already toasts the
      // error). Keep the wizard's values + step intact so the user
      // can correct the issue without re-entering everything.
      return;
    }

    clearDraft();
    setValues(INITIAL_VALUES);
    /**
     * The parent (`FinancingCarrierPage.handleSubmit`) owns the
     * post-submit URL transition — it does a single `setSearchParams`
     * that switches `tab=history` AND deletes `apply_step` in the
     * same update. Calling `setStep('invoice')` here as well would
     * race the parent's call (both read the same stale
     * `searchParamsRef`) and clobber `tab=history` back to
     * `tab=apply`, leaving the user stuck on the Apply tab.
     */
    setCompleted(new Set());
  };

  const isLast = step === 'review';

  return (
    <section
      ref={sectionRef}
      id="apply"
      className="scroll-mt-20 rounded-2xl border border-gray-200 bg-white p-5 sm:p-7">
      <header className="mb-6 flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100">
          <HandCoinsIcon className="size-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
            {t('apply_form.title')}
          </h2>
          <p className="mt-0.5 text-sm text-gray-600">{t('apply_form.description')}</p>
        </div>
      </header>

      <WizardStepper
        current={step}
        completed={completed}
        onJumpTo={s => setStep(s)}
        visibleSteps={STEP_KEYS}
      />

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div className="rounded-2xl border border-gray-100 bg-gray-50/40 p-4 sm:p-5">
          {step === 'invoice' && (
            <InvoiceFieldset values={values} onChange={onChange} />
          )}
          {step === 'terms' && (
            <TermsFieldset
              values={values}
              onChange={onChange}
              onFinancingProfileSaved={() => void orgProfileQuery.refetch()}
            />
          )}
          {step === 'review' && <ReviewStep values={values} onEdit={s => setStep(s)} />}
        </div>

        {/*
         * Hard-block banner — only shown on the Review step and only
         * when something is still blocking. Lists exactly which
         * requirement is missing + a deep-link back to the step that
         * owns it. Anchored at the bottom of the wizard (above the
         * action row) so the user reads it right before reaching for
         * Submit.
         */}
        {/*
         * Step-incomplete banner — shown on Review when one of the
         * three wizard steps is invalid but no hard block is active.
         * Common cause: sessionStorage hydration restored a draft
         * without the invoice file (Files can't survive a reload).
         * Without this banner the submit just sits disabled and the
         * carrier has no idea why.
         */}
        {isLast && !effectiveHardBlocked && !allValid ? (
          <div
            className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900"
            role="alert">
            <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="font-semibold">
                {t('apply_form.submit_incomplete.title')}
              </p>
              <ul className="mt-1.5 space-y-1 text-xs">
                {!isStepValid('invoice', values, savedProfileComplete, heroGates) ? (
                  <BlockedRow
                    onClick={() => setStep('invoice')}
                    label={t('apply_form.submit_incomplete.invoice')}
                  />
                ) : null}
                {!isStepValid('terms', values, savedProfileComplete, heroGates) ? (
                  <>
                    {!savedProfileComplete ? (
                      <BlockedRow
                        onClick={() => setStep('terms')}
                        label={t('apply_form.submit_incomplete.profile')}
                      />
                    ) : null}
                    {!(
                      Boolean(values.requestedAmount) &&
                      Boolean(values.requestedTermKey)
                    ) ? (
                      <BlockedRow
                        onClick={() => setStep('terms')}
                        label={t('apply_form.submit_incomplete.terms')}
                      />
                    ) : null}
                  </>
                ) : null}
              </ul>
            </div>
          </div>
        ) : null}

        {isLast && !effectiveHardBlocked ? (
          <Label
            htmlFor="apply-confirm-accuracy"
            className="flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 text-sm font-normal text-gray-700 hover:border-emerald-300 hover:bg-emerald-50/30">
            <Checkbox
              id="apply-confirm-accuracy"
              checked={confirmedAccuracy}
              onCheckedChange={v => setConfirmedAccuracy(v === true)}
              className="mt-0.5"
            />
            <span className="leading-snug">
              {t('apply_form.review_summary.confirm_accuracy')}
            </span>
          </Label>
        ) : null}

        {isLast && effectiveHardBlocked ? (
          <div
            className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50/60 p-4 text-sm text-red-900"
            role="alert">
            <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="font-semibold">
                {t('apply_form.submit_blocked.title')}
              </p>
              <ul className="mt-1.5 space-y-1 text-xs">
                {hardBlocks.carrierLegal ? (
                  <BlockedRow
                    onClick={() => setStep('terms')}
                    label={t('apply_form.submit_blocked.carrier_legal')}
                  />
                ) : null}
                {hardBlocks.carrierBanking ? (
                  <BlockedRow
                    onClick={() => setStep('terms')}
                    label={t('apply_form.submit_blocked.carrier_banking')}
                  />
                ) : null}
                {!!gateClientId && hardBlocks.clientLegal ? (
                  <BlockedRow
                    onClick={() => setStep('invoice')}
                    label={t('apply_form.submit_blocked.client_legal')}
                  />
                ) : null}
                {!!gateClientId && hardBlocks.agreement ? (
                  <BlockedRow
                    onClick={() => setStep('invoice')}
                    label={t('apply_form.submit_blocked.agreement')}
                  />
                ) : null}
                {heroGatesApply && hardBlocks.heroInvoiceProof ? (
                  <BlockedRow
                    onClick={() => setStep('invoice')}
                    label={t('apply_form.submit_blocked.hero_invoice_proof')}
                  />
                ) : null}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            disabled={step === 'invoice'}>
            <ArrowLeftIcon className="me-2 size-4 rtl:rotate-180" />
            {t('apply_form.back')}
          </Button>

          {isLast ? (
            <Button
              type="submit"
              disabled={
                !allValid ||
                isSubmitting ||
                effectiveHardBlocked ||
                !confirmedAccuracy
              }
              className="bg-emerald-700 text-white hover:bg-emerald-800">
              {!isSubmitting && <HandCoinsIcon className="me-2 size-4" />}
              {isSubmitting ? t('apply_form.submitting') : t('apply_form.submit')}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={goNext}
              disabled={!stepValid}
              className="bg-emerald-700 text-white hover:bg-emerald-800">
              {t('apply_form.next')}
              <ArrowRightIcon className="ms-2 size-4 rtl:rotate-180" />
            </Button>
          )}
        </div>
      </form>
    </section>
  );
});

/**
 * Single missing-requirement row inside the submit-blocked banner.
 * Renders as a bullet + a text-button "Fix" link that jumps the
 * wizard to the step owning that requirement. Kept inline (not
 * exported) — its semantics are tightly coupled to the banner.
 */
function BlockedRow({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-red-500" />
      <span className="flex-1">{label}</span>
      <button
        type="button"
        onClick={onClick}
        className="text-xs font-semibold text-red-700 underline-offset-2 hover:underline">
        →
      </button>
    </li>
  );
}
